import * as React from "react";
import * as ReactDOM from "react-dom";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import APIConnector from "../../../apiconnector";
import CommonVariables from "../../../variables";
import Notifications from "../../../Notifications/Messages";
import { Filter, Operators, TextFilter } from "@progress/kendo-react-data-tools";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { AccountCircle, TableChart } from "@mui/icons-material";
import ContactModal from "./ContactModal";
import { emptyContact, getContactId, mapContactResponse } from "./contactHelper";

/**
 * Contact Component enables users to view, add, and edit contacts.
 */

const Contact = (props) => {

    const contactFields = [
        {
            name: "fullname",
            label: "Full Name",
            filter: TextFilter,
            operators: Operators.text,
            width: "180",
        },
        {
            name: "telephone1",
            label: "Telephone",
            filter: TextFilter,
            operators: Operators.text,
            width: "140",
        },
        {
            name: "emailaddress1",
            label: "Email",
            filter: TextFilter,
            operators: Operators.text,
            width: "220",
        },
        {
            name: "shipping_method",
            label: "Shipping Method",
            filter: TextFilter,
            operators: Operators.text,
            width: "170",
        },
        {
            name: "account_name",
            label: "Account",
            filter: TextFilter,
            operators: Operators.text,
            width: "180",
        },
        {
            name: "address1_city",
            label: "City",
            filter: TextFilter,
            operators: Operators.text,
            width: "140",
        },
        {
            name: "tt_stateprovinceid",
            label: "State/Province",
            filter: TextFilter,
            operators: Operators.text,
            width: "160",
        }
    ];

    const contactGridFields = contactFields
        .filter((field) => field.name)
        .map(({ width, ...field }) => field);

    const renderContactColumns = () => contactFields.map((field) => (
        <Column
            key={field.name}
            field={field.name}
            width={field.width}
            title={field.label}
        />
    ));

    //#region " Initialization of State Values "

    const initialFilter = {
        logic: "and",
        filters: [{
            field: "fullname",
            operator: "contains",
            value: '',
        }]
    };

    const [filter, setFilter] = React.useState(initialFilter);
    const [sort, setSort] = React.useState([]);
    const [data, setData] = React.useState([]);
    const [showContactDialog, setShowContactDialog] = React.useState(false);
    const [newContact, setNewContact] = React.useState(emptyContact);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [isEditMode, setIsEditMode] = React.useState(false);
    const [editingContactItem, setEditingContactItem] = React.useState(null);
    const [deleteContactDialogOpen, setDeleteContactDialogOpen] = React.useState(false);
    const [deleteContactItem, setDeleteContactItem] = React.useState(null);

    const toolbarButtonBorderColor = "#8b96c7";
    const toolbarButtonHoverBorderColor = "#574ea8";
    const toolbarButtonStyle = {
        marginLeft: "15px",
        marginBottom: "3px",
        font: "caption",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        border: `1px solid ${toolbarButtonBorderColor}`,
        borderRadius: "4px",
        padding: "6px 8px",
        backgroundColor: "#ebefff",
        cursor: "pointer",
        lineHeight: "20px",
        verticalAlign: "middle"
    };
    const toolbarIconStyle = {
        display: "block",
        borderColor: "transparent",
        color: "#5454ec",
        cursor: "pointer",
        fontSize: "18px",
        width: "18px",
        height: "18px"
    };
    const handleToolbarButtonMouseEnter = (event) => {
        event.currentTarget.style.borderColor = toolbarButtonHoverBorderColor;
    };
    const handleToolbarButtonMouseLeave = (event) => {
        event.currentTarget.style.borderColor = toolbarButtonBorderColor;
    };

    //#endregion

    //#region " On Load "

    React.useEffect(() => {
        refreshContacts();
    }, []);

    const refreshContacts = (msg) => {
        props.onLoaderRaise(true, "Loading contact list ...", { marginTop: "145px" });
        APIConnector.Get(CommonVariables.urls.GetAllContacts).then(function (response) {
            props.onLoaderRaise(false);
            if (!CommonVariables.isError(response))
                return;

            setData(mapContactResponse(response));

            if (msg) {
                Notifications.ShowSuccessMessage(msg);
            }
        });
    };

    //#endregion

    //#region " Print Functionality "

    let gridPDFExport;
    const exportPDF = () => {
        if (gridPDFExport) {
            gridPDFExport.save(data);
        }
    };

    //#endregion

    //#region " Export to Excel functionality "

    let _export;
    const exportExcel = () => {
        _export.save(data);
    };

    //#endregion

    //#region " Contact Dialog Events "

    const openAddContactDialog = () => {
        setIsEditMode(false);
        setEditingContactItem(null);
        setValidationErrors({});
        setNewContact({ ...emptyContact });
        setShowContactDialog(true);
    };

    const openEditContactDialog = (contactItem) => {
        setIsEditMode(true);
        setEditingContactItem(contactItem);
        setValidationErrors({});
        setNewContact({
            ...emptyContact,
            ...contactItem
        });
        setShowContactDialog(true);
    };

    const closeContactDialog = () => {
        setShowContactDialog(false);
        setIsEditMode(false);
        setEditingContactItem(null);
        setValidationErrors({});
        setNewContact({ ...emptyContact });
    };

    const handleContactSaved = () => {
        closeContactDialog();
        refreshContacts(isEditMode ? "Contact updated successfully" : "Contact added successfully");
    };

    const openDeleteContactDialog = (contactItem) => {
        setDeleteContactItem(contactItem);
        setDeleteContactDialogOpen(true);
    };

    const closeDeleteContactDialog = () => {
        setDeleteContactItem(null);
        setDeleteContactDialogOpen(false);
    };

    const confirmDeleteContact = () => {
        const contactId = getContactId(deleteContactItem);

        if (!contactId) {
            Notifications.ShowErrorMessage("Contact Id was not found.");
            return;
        }

        props.onLoaderRaise(true, "Deleting contact ...", { marginTop: "145px" });
        APIConnector.Post(CommonVariables.urls.DeleteContact, { contactid: [contactId]}).then(function (response) {
            props.onLoaderRaise(false);
            if (!CommonVariables.isError(response))
                return;

            if (response === true || response?.isSuccess || response?.IsSuccess) {
                closeDeleteContactDialog();
                refreshContacts("Contact deleted successfully");
            }
            else {
                Notifications.ShowErrorMessage("Something went wrong. Please try again.");
            }
        });
    };


    //#endregion

    //#region " Grid "

    const MyEditCommandCell = (props) => (
        <EditCommandCell {...props} enterEdit={enterEdit} />
    );

    const MyDeleteCommandCell = (props) => (
        <DeleteCommandCell {...props} openDeleteDialog={openDeleteContactDialog} />
    );

    const EditCommandCell = (props) => {
        return (
            <td style={{ textAlign: "center" }}>
                <span title="Edit">
                    <EditIcon onClick={() => props.enterEdit(props.dataItem)} title="Edit" style={{ display: "inline-block", borderColor: "transparent", color: "#5454ec", cursor: "pointer" }} />
                </span>
            </td>
        );
    };

    const DeleteCommandCell = (props) => {
        return (
            <td style={{ textAlign: "center" }}>
                <span title="Delete">
                    <DeleteIcon onClick={() => props.openDeleteDialog(props.dataItem)} title="Delete" style={{ display: "inline-block", borderColor: "transparent", color: "#d51923", cursor: "pointer" }} />
                </span>
            </td>
        );
    };

    const enterEdit = (item) => {
        openEditContactDialog(item);
    };

    const gridData = orderBy(filterBy(data, filter), sort);

    const grid = (
        <Grid className="rounded-2xl border-b-4" style={{ height: (window.innerHeight - 317) + "px" }}
            data={gridData}
            sortable={{ allowUnsort: true, mode: "single" }}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            pageable={true}
            take={1000}
            total={gridData.length}
            resizable={true}
            selectable={{
                enabled: true,
                drag: false,
                cell: false,
                mode: "single"
            }}>
            <GridNoRecords>
                No Contacts available
            </GridNoRecords>
            {renderContactColumns()}
            <Column cell={MyEditCommandCell} width="60" title="Edit" sortable={false} />
            <Column cell={MyDeleteCommandCell} width="70" title="Delete" sortable={false} />
        </Grid>
    );

    //#endregion

    return (
        <div className="PageStyle">
            <h2 className="inline-block pt-1 primary-heading">Contacts</h2>
            <div className="row" style={{ "marginRight": "auto" }}>
                <div className="col-sm-6">
                    <Filter
                        value={filter}
                        onChange={(e) => setFilter(e.filter)}
                        fields={contactGridFields}
                    />
                </div>
                <div className="col-sm-6" style={{ marginTop: "auto", marginRight: "auto", paddingRight: 0, textAlign: "end" }}>
                    <span
                        title="Add Contact"
                        onClick={openAddContactDialog}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Add Contact</span>
                        <AccountCircle style={toolbarIconStyle} />
                    </span>
                    <span
                        title="Print"
                        onClick={exportPDF}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Print</span>
                        <PrintIcon style={toolbarIconStyle} />
                    </span>
                    <span
                        title="Excel"
                        onClick={exportExcel}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Export to Excel</span>
                        <TableChart style={toolbarIconStyle} />
                    </span>
                    <span
                        title="Refresh grid"
                        onClick={refreshContacts}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Refresh</span>
                        <RefreshIcon style={toolbarIconStyle} />
                    </span>
                </div>
            </div>

            {grid}

            {showContactDialog && (
                <ContactModal
                    isOpen={showContactDialog}
                    isEditMode={isEditMode}
                    initialContact={newContact}
                    editingContactItem={editingContactItem}
                    parentCustomerId={newContact.parentCustomerId || newContact.parentcustomerid || newContact.account_accountid || ""}
                    onClose={closeContactDialog}
                    onSaveSuccess={handleContactSaved}
                    onLoaderRaise={props.onLoaderRaise}
                />
            )}

            {deleteContactDialogOpen && (
                <div
                    onClick={closeDeleteContactDialog}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 12000,
                        display: "flex",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "auto"
                    }}
                >
                    <div
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            width: "360px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #d9d9d9",
                            borderRadius: "6px",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                            pointerEvents: "auto"
                        }}
                    >
                        <div
                            style={{
                                padding: "10px 14px",
                                borderBottom: "1px solid #e5e5e5",
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <span>Confirm Delete</span>
                            <button
                                type="button"
                                onClick={closeDeleteContactDialog}
                                style={{
                                    border: "none",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    lineHeight: "18px"
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: "14px" }}>
                            Are you sure you want to delete this contact?
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                                padding: "10px 14px",
                                borderTop: "1px solid #e5e5e5"
                            }}
                        >
                            <Button onClick={closeDeleteContactDialog}>Cancel</Button>
                            <Button themeColor="error" onClick={confirmDeleteContact}>Delete</Button>
                        </div>
                    </div>
                </div>
            )}

            <GridPDFExport ref={(pdfExport) => (gridPDFExport = pdfExport)}>
                {renderContactColumns()}
                {grid}
            </GridPDFExport>
            <ExcelExport data={data} ref={(exporter) => { _export = exporter; }}>
                {renderContactColumns()}
            </ExcelExport>
        </div>
    );
};

export default Contact;